import { z } from 'zod';
import { contentIdSchema } from './content-id.js';
/**
 * Инвентарь персонажа: что у него есть и что из этого надето.
 *
 * Инвентарь не хранится снимком. Он такая же свёртка журнала (D4),
 * как и остальной лист: стартовое снаряжение разворачивается из шага
 * создания, а взятия, отказы, надевания и снятия — отдельные шаги.
 * Отсюда то же свойство, ради которого журнал и заведён: исправили
 * состав набора в контенте — исправились все персонажи сразу,
 * без миграции.
 *
 * Действие записывается ровно двумя выборами шага: `inventory.<что
 * сделали>` со ссылкой на предмет и, если действию нужно число,
 * `inventory.count`. Ссылка лежит значением выбора отдельной строкой,
 * а не полем внутри объекта, — так её видит сборщик эффектов
 * (`collectEffects`) и так же её находит сервер, поднимая контент
 * для свёртки.
 */
/** Что сделали с предметом. */
export const inventoryActionKindSchema = z.enum([
    /** Взять предмет: количество прибавляется к тому, что есть. */
    'take',
    /** Выбросить целиком: количество и признак «надето» уходят вместе. */
    'drop',
    /** Задать количество: `0` равносилен `drop`. */
    'set',
    'equip',
    'unequip',
]);
/** Ключ выбора в шаге журнала: `inventory.equip`. */
export function inventorySlot(kind) {
    return `inventory.${kind}`;
}
/** Выбор с количеством: стоит рядом с действием в том же шаге. */
export const INVENTORY_COUNT_SLOT = 'inventory.count';
export const inventoryActionSchema = z.strictObject({
    kind: inventoryActionKindSchema,
    item: contentIdSchema,
    /** Нужен `take` и `set`; у остальных действий не читается. */
    count: z.number().int().nonnegative().optional(),
});
/**
 * Отказ в действии с инвентарём.
 *
 * Как и у создания (`CreationProblem`), наружу идёт код: текст для
 * человека собирает интерфейс через i18next (D11). Сообщение здесь —
 * для того, кто читает ответ глазами.
 */
export const inventoryProblemCodeSchema = z.enum([
    /** Записи с таким ключом нет среди переданного контента. */
    'unknown-item',
    /** Предмета нет у персонажа: снять или выбросить нечего. */
    'not-in-inventory',
    /** Позиция без записи контента: с ней ничего сделать нельзя. */
    'not-a-record',
    'already-equipped',
    'not-equipped',
    /** Надеть можно доспех, щит или оружие; мешок надеть нельзя. */
    'not-equippable',
    /** Второй доспех поверх первого: SRD такого не знает. */
    'armor-already-worn',
    /** Второй щит: рука одна. */
    'shield-already-held',
    /** Количество вне допустимого. */
    'bad-count',
]);
export const inventoryProblemSchema = z.strictObject({
    code: inventoryProblemCodeSchema,
    /** Предмет, о котором речь: интерфейс подсвечивает его строку. */
    item: z.string().min(1),
    /**
     * Мешающий предмет: у «второго доспеха» это тот доспех, который
     * уже надет. Без него отказ пришлось бы искать глазами.
     */
    blockedBy: contentIdSchema.nullable(),
    message: z.string().min(1),
});
/** Вид предмета: тем же словом, что и `mechanics.category` записи. */
export const inventoryCategorySchema = z.enum([
    'armor',
    'shield',
    'weapon',
    'gear',
    'tool',
    'magic',
]);
export const inventoryEntrySchema = z.strictObject({
    /**
     * Запись контента; `null` — позиция стартового набора, у которой
     * записи нет вовсе («Spellbook»: книги заклинаний в таблицах
     * снаряжения SRD нет, `content/srd/REPORT.md`, раздел 6.3).
     * С такой позицией ничего сделать нельзя — её не на что сослать.
     */
    id: contentIdSchema.nullable(),
    /** Название из записи либо из набора, если записи нет. */
    name: z.string().min(1),
    quantity: z.number().int().positive(),
    equipped: z.boolean(),
    category: inventoryCategorySchema.nullable(),
    /** Вес одной штуки; `null` — вес зависит от варианта (REPORT, раздел 3). */
    weightLb: z.number().nonnegative().nullable(),
    /**
     * Описание из записи контента — на языке источника. Показывается
     * по тапу: «что это вообще такое» спрашивают у половины позиций
     * стартового набора, а ходить за ответом в книгу — не ответ.
     * `null` — у позиции нет записи, и описывать нечего.
     */
    text: z.string().nullable(),
    /** Наш перевод описания, если он есть (D32); `null` — перевода нет. */
    textRu: z.string().nullable(),
});
export const inventorySchema = z.strictObject({
    entries: z.array(inventoryEntrySchema),
    /**
     * Монеты приходят из стартового набора («and 19 GP») и лежат
     * отдельной величиной: это не предмет, у него нет ни веса записи,
     * ни возможности его надеть.
     */
    coinsGp: z.number().nonnegative(),
    /** Вес всего, что есть, включая надетое. */
    totalWeightLb: z.number().nonnegative(),
    /** Сколько персонаж несёт: Сила × множитель размера. */
    carryLb: z.number().nonnegative(),
    /** Сколько тащит, волочит или поднимает — вдвое больше. */
    dragLb: z.number().nonnegative(),
    /**
     * Вес перевалил за переносимый. Что из этого следует, считает
     * движок: правило «скорость не больше пяти футов» лежит эффектом
     * записи правил и приходит уже посчитанной величиной `speed`
     * (SRD 5.2, Rules Glossary, Carrying Capacity, с. 178).
     */
    overCarry: z.boolean(),
    /**
     * Позиции, у которых нет веса: их не посчитать, и молчать об этом
     * нельзя — итог был бы занижен, а почему, никто бы не увидел.
     */
    weightUnknown: z.array(z.string().min(1)),
});
//# sourceMappingURL=inventory.js.map